import { ThunkAction } from 'redux-thunk';
import * as Bixby from '$/core';
import { AppState } from './..';
import { ClientAdminService } from '$/services';

import {
  CLIENTS_LOADING,
  CLIENTS_SUCCESS,
  CLIENTS_FAILURE,
  CLIENT_LOADING,
  CLIENT_SUCCESS,
  CLIENT_FAILURE,
  CLIENT_DELETE,
  CLIENT_DELETE_SUCCESS,
  CLIENT_DELETE_FAILURE,
  CREATE_CLIENT,
  CREATE_CLIENT_SUCCESS,
  CREATE_CLIENT_FAILURE,
  CLIENT_UPDATE_SUCCESS,
  CLIENT_UPDATE_FAILURE,
  CLIENT_USERS_LOADING,
  CLIENT_USERS_SUCCESS,
  CLIENT_USERS_FAILURE,
} from './types';
import { ClientSlim, UpdateClientRequest } from '$/core/models';
import { toastError, toastSuccess } from '$/store/toast/actions';

// action creators
export const loadClientsAction = () => {
  return {
    type: CLIENTS_LOADING,
  };
};

export const clientsSuccessAction = (clients: Bixby.Models.ClientSlim[]) => {
  return {
    type: CLIENTS_SUCCESS,
    payload: clients,
  };
};

export const clientsFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: CLIENTS_FAILURE,
    payload: error,
    error: true,
  };
};

export const loadClientAction = () => {
  return {
    type: CLIENT_LOADING,
  };
};

export const clientSuccessAction = (client?: Bixby.Models.Client) => {
  return {
    type: CLIENT_SUCCESS,
    payload: client,
  };
};

export const clientFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: CLIENT_FAILURE,
    payload: error,
    error: true,
  };
};

export const createClientAction = (request: Bixby.Models.CreateClientRequest) => {
  return {
    type: CREATE_CLIENT,
    payload: request,
  };
};

export const createClientSuccessAction = (client: Bixby.Models.Client) => {
  return {
    type: CREATE_CLIENT_SUCCESS,
    payload: client,
  };
};

export const createClientFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: CREATE_CLIENT_FAILURE,
    payload: error,
  };
};

export const deleteClientAction = () => {
  return {
    type: CLIENT_DELETE,
  };
};

export const deleteClientSuccessAction = () => {
  return {
    type: CLIENT_DELETE_SUCCESS,
  };
};

export const deleteClientFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: CLIENT_DELETE_FAILURE,
    payload: error,
    error: true,
  };
};

export const clientUpdateSuccessAction = (client: Bixby.Models.Client) => {
  return {
    type: CLIENT_UPDATE_SUCCESS,
    payload: client,
  };
};
export const clientUpdateFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: CLIENT_UPDATE_FAILURE,
    payload: error,
    error: true,
  };
};
export const clientUsersLoading = () => {
  return {
    type: CLIENT_USERS_LOADING,
  };
};
export const clientUsersSuccess = (payload: Bixby.Models.UserSlim[]) => {
  return {
    type: CLIENT_USERS_SUCCESS,
    payload,
  };
};
export const clientUsersFailure = (payload: Bixby.Models.BixbyError) => {
  return {
    type: CLIENT_USERS_FAILURE,
    payload,
  };
};

// thunk actions
export const getClients =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async dispatch => {
    dispatch(loadClientsAction());
    try {
      const companies = await ClientAdminService.getAll();
      dispatch(clientsSuccessAction(companies));
    } catch (error: any) {
      dispatch(clientsFailureAction(error));
    }
  };

export const deleteClient =
  (client: Bixby.Models.Client): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
    async dispatch => {
      dispatch(deleteClientAction());
      try {
        await ClientAdminService.deleteById(client.id);
        await dispatch(getClients());
        dispatch(deleteClientSuccessAction());
        await dispatch(toastSuccess(`Deleted client ${client.name}`));
      } catch (error: any) {
        dispatch(deleteClientFailureAction(error));
      }
    };

export const createClient =
  (request: Bixby.Models.CreateClientRequest): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
    async dispatch => {
      await dispatch(createClientAction(request));
      try {
        const client = await ClientAdminService.create(request);
        await dispatch(getClients());
        await dispatch(createClientSuccessAction(client));
        await dispatch(toastSuccess(`Created client ${client.name}`));
      } catch (error: any) {
        dispatch(createClientFailureAction(error));
        await dispatch(toastError('Failed to create client'));
      }
    };

// TODO - refactor
export const setCurrentClient =
  (clientSlim?: ClientSlim): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
    async (dispatch, getState) => {
      await dispatch(loadClientAction());
      try {
        if (clientSlim && clientSlim.id) {
          if (clientSlim.id !== (getState().clientState.currentClient || ({} as any)).id) {
            const client = await ClientAdminService.getById(clientSlim.id);
            await dispatch(clientSuccessAction(client));
          }
        } else {
          await dispatch(clientSuccessAction(undefined));
        }
      } catch (error: any) {
        dispatch(clientFailureAction(error));
      }
    };

export const updateClient =
  (id: string, request?: UpdateClientRequest): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
    async dispatch => {
      try {
        const client = request ? await ClientAdminService.putById(id, request) : undefined;
        if (!client) {
          throw new Error(`Failed to update client with id ${id}`);
        }
        await dispatch(clientUpdateSuccessAction(client));
        await dispatch(toastSuccess(`Updated ${client.name}`));
      } catch (error: any) {
        dispatch(clientUpdateFailureAction(error));
        await dispatch(toastError('Failed to update client'));
      }
    };

export const getClientUsers =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async (dispatch, getState) => {
    const { currentClient } = getState().clientState;
    if (!currentClient) {
      return;
    }
    dispatch(clientUsersLoading());
    try {
      const users = await ClientAdminService.getUsersByClientId(currentClient.id);
      dispatch(clientUsersSuccess(users));
    } catch (error: any) {
      dispatch(clientUsersFailure(error));
      dispatch(toastError(`Failed to get users for ${currentClient.name}`));
    }
  };

export const reloadCurrentClient =
  (): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> => async (dispatch, getState) => {
    await dispatch(loadClientAction());
    try {
      const state = getState();
      if (state && state.clientState && state.clientState.currentClient && state.clientState.currentClient.id) {
        const client = await ClientAdminService.getById(state.clientState.currentClient.id);
        await dispatch(clientSuccessAction(client));
      }
    } catch (error: any) {
      dispatch(clientFailureAction(error));
    }
  };
